// QualityManagementPage.js
import { useState, useEffect, useCallback } from 'react';
import QMTrendingTiles from './QMTrendingTiles';
import QMTrendingCharts from './QMTrendingCharts';
import QMTrendingFilter from './QMTrendingFilter';
import Tools from '../utils/Tools';
import { useFilter } from '../contexts/FilterContext';
import Grid from "./MainGrid";

function QualityManagementPage() {
    const { filters, setFilters } = useFilter();
    const [loadingSummary, setLoadingSummary] = useState(true);
    const [loading, setLoading] = useState(true); // Add separate loading state for grid
    const [qmData, setQmData] = useState({});
    const [dateGroupType, setDateGroupType] = useState(null);
    const [qmGridData, setQmGridData] = useState([]); // Grid data
    const [agents, setAgents] = useState([]);
    const [categories, setCategories] = useState([]);
    const [queues, setQueues] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortModel, setSortModel] = useState([]);

    function formatDateToYYYYMMDD(date) {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    const handlePageChange = useCallback((newPage) => {
        setCurrentPage(newPage);
    }, []);

    const handleSortChange = useCallback((newSortModel) => {
        setSortModel(newSortModel);
    }, []);

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1); // Reset to first page on filter change
    }, [setFilters]);

    // Fetch QM summary data (for tiles and charts)
    useEffect(() => {
        setLoadingSummary(true);
        const queryParams = new URLSearchParams();

        if (filters.agent && filters.agent.value) {
            queryParams.append('agent', filters.agent.value);
        }
        if (filters.dateRange) {
            queryParams.append('dateRange', filters.dateRange.value);
        }
        if (filters.dateStart) {
            queryParams.append('dateStart', formatDateToYYYYMMDD(filters.dateStart));
        }
        if (filters.dateEnd) {
            queryParams.append('dateEnd', formatDateToYYYYMMDD(filters.dateEnd));
        }
        if (filters.category && filters.category.length > 0) {
            filters.category.forEach(cat => {
                queryParams.append("category", cat.value);
            });
        }
        if (filters.queue && filters.queue.length > 0) {
            filters.queue.forEach(queue => {
                queryParams.append("queue", queue.value);
            });
        }
        if (!filters.excludeWeekends) {
            queryParams.append("excludeWeekends", filters.excludeWeekends);
        }

        queryParams.append("isQm", true);

        Tools.sendRequest("get-qm-trends", queryParams.toString())
            .then((dataResponse) => {
                console.log('DEBUG - GOT QM DATA', dataResponse);

                // Set the complete data response
                setQmData(dataResponse || {});

                // Process agents from callsByAgent
                if (dataResponse.callsByAgent) {
                    const agentOptions = Object.entries(dataResponse.callsByAgent)
                        .map(([agent, count]) => ({
                            value: agent,
                            label: agent,
                            count: count,
                        }))
                        .sort((a, b) => b.count - a.count);
                    setAgents(agentOptions);
                }

                // Process categories from callsByCategory
                if (dataResponse.callsByCategory) {
                    const categoryOptions = Object.entries(dataResponse.callsByCategory)
                        .map(([category, count]) => ({
                            value: category,
                            label: category.charAt(0).toUpperCase() + category.slice(1),
                            count: count,
                        }))
                        .sort((a, b) => b.count - a.count);
                    setCategories(categoryOptions);
                }

                // Process queues from callsByQueue
                if (dataResponse.callsByQueue) {
                    const queueOptions = Object.entries(dataResponse.callsByQueue)
                        .map(([queue, count]) => ({
                            value: queue,
                            label: queue,
                            count: count,
                        }))
                        .sort((a, b) => b.count - a.count);
                    setQueues(queueOptions);
                }

                if (dataResponse.dateGroupType) {
                    setDateGroupType(dataResponse.dateGroupType)
                }
                setLoadingSummary(false);
            })
            .catch(error => {
                console.error('Error fetching QM data:', error);
                setLoadingSummary(false);
            });
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        const queryParams = new URLSearchParams();

        // Add filter parameters
        if (filters.agent && filters.agent.value) {
            queryParams.append('agent', filters.agent.value);
        }
        if (filters.dateRange) {
            queryParams.append('dateRange', filters.dateRange.value);
        }
        if (filters.dateStart) {
            queryParams.append('dateStart', formatDateToYYYYMMDD(filters.dateStart));
        }
        if (filters.dateEnd) {
            queryParams.append('dateEnd', formatDateToYYYYMMDD(filters.dateEnd));
        }
        if (filters.category && filters.category.length > 0) {
            filters.category.forEach(cat => {
                queryParams.append("category", cat.value);
            });
        }
        if (filters.queue && filters.queue.length > 0) {
            filters.queue.forEach(queue => {
                queryParams.append("queue", queue.value);
            });
        }
        if (!filters.excludeWeekends) {
            queryParams.append("excludeWeekends", filters.excludeWeekends);
        }

        queryParams.append("page", currentPage);

        queryParams.append("isQm", true);

        if (sortModel && sortModel.length > 0) {
            sortModel.forEach((sortItem) => {
                queryParams.append("sortBy", sortItem.colId);
                queryParams.append("sortOrder", sortItem.sort);
            });
        }

        Tools.sendRequest("get-data", queryParams.toString())
            .then((response) => {
                setQmGridData(response.data || []);
                setTotalRecords(response.totalRecords || 0);
                setTotalPages(response.totalPages || 0);
                setCurrentPage(response.currentPage || 1);
                setLoading(false);
            });
    }, [filters, currentPage, sortModel]);

    return (
        <div className="container mx-auto p-4">
            <QMTrendingFilter
                filters={filters}
                setFilters={handleFilterChange}
                agents={agents}
                categories={categories}
                queues={queues}
            />

            {loadingSummary || loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="loader"></div>
                </div>
            ) : (
                <>
                    <QMTrendingTiles totalCalls={totalRecords} qmData={qmData} />
                    <QMTrendingCharts
                        dateGroupType={dateGroupType}
                        dateRange={filters.dateRange.value}
                        trendData={qmData}
                        filters={filters}
                    />
                    <Grid
                        data={qmGridData}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalRecs={totalRecords}
                        onPageChange={handlePageChange}
                        onSortChange={handleSortChange}
                        filters={filters}
                        sortModel={sortModel}
                        referrer={"/quality-management"}
                        hiddenFields={
                            [
                                "agent_sentiment",
                                "customer_sentiment",
                                "agent_performance_score",
                                "customer_experience_score",
                                "customer_final_satisfaction"
                            ]
                        }
                        widthOverrides={{ contact_id: 300 }}
                    />
                </>
            )}
        </div>
    );
}

export default QualityManagementPage;